import { apiClient, ApiResponse } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

/**
 * 관리자 로그인 요청.
 * feat/admin-email-login — 식별자는 email (기존 username 제거).
 * latitude/longitude는 LOGIN 성공 로그용 선택 필드. 한쪽만 있으면 서버가 둘 다 무시한다.
 */
export interface AdminAuthRequest {
  email: string;
  password: string;
  latitude?: number | null;
  longitude?: number | null;
}

export interface RefreshRequest {
  refreshToken: string;
}

/** 로그인·비밀번호 변경·refresh 공통 토큰 응답 */
export interface AuthTokenResponse {
  token: string;
  refreshToken: string;
  /** 로그인 응답에만 포함될 수 있음 */
  email?: string;
  /**
   * 로그인 응답 전용 — 임시 비밀번호 등으로 강제 변경 필요.
   * GET /admin/auth/me 에는 포함되지 않으므로 로그인 시점에 store에 보관한다.
   */
  mustChangePassword?: boolean;
}

/** @deprecated AuthTokenResponse 사용 — 하위 호환 alias */
export type AuthResponse = AuthTokenResponse;

export interface AdminChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface AdminMyAccountUpdateRequest {
  /** 변경된 이름만 전달 (미변경 시 생략) */
  name?: string;
  /** 변경된 연락처만 전달 (미변경 시 생략) */
  phone?: string;
}

/** 메뉴별 유효 권한 — `/admin/auth/me` permissions[].effectivePermission */
export type EffectivePermission = 'NONE' | 'READ' | 'WRITE';

/**
 * `/admin/auth/me` 메뉴 권한 항목.
 * topLevel 메뉴와 하위 menuCode로 사이드바·화면 접근을 제어한다.
 * menuName/urlPath/visible 등은 동적 사이드바 구성에 사용한다.
 */
export interface MenuPermission {
  menuId?: number;
  menuCode: string;
  parentMenuCode: string | null;
  topLevel: boolean;
  effectivePermission: EffectivePermission;
  overridden?: boolean;
  menuName?: string | null;
  urlPath?: string | null;
  displayOrder?: number;
  visible?: boolean;
  accessScope?: 'GENERAL' | 'ADMIN_ONLY';
}

/**
 * `/admin/auth/me` 응답.
 * 세션 복원·메뉴 권한 판별에 쓰이므로 authStore에 persist한다.
 */
export interface AdminUserInfo {
  username: string;
  roles: string[];
  email?: string;
  name?: string;
  phone?: string;
  department?: string;
  passwordChangedAt?: string | null;
  permissions?: MenuPermission[];
  /** 임시 비밀번호 등으로 비밀번호 변경 필수 */
  mustChangePassword?: boolean;
  /** 임시 비밀번호 만료 */
  temporaryPasswordExpired?: boolean;
}

export const authService = {
  login: async (
    credentials: AdminAuthRequest,
  ): Promise<ApiResponse<AuthTokenResponse>> => {
    const response = await apiClient.post(
      createApiPath.auth.login(),
      credentials,
    );

    return {
      data: response.data,
      success: true,
    };
  },

  /**
   * Access Token 재발급 — POST /admin/auth/refresh.
   * 실패(400 INVALID_REFRESH_TOKEN, 401 SESSION_EXPIRED 등)는
   * http-client가 재시도 없이 세션을 종료한다.
   */
  refresh: async (
    refreshToken: string,
  ): Promise<ApiResponse<AuthTokenResponse>> => {
    const response = await apiClient.post(createApiPath.auth.refresh(), {
      refreshToken,
    });

    return {
      data: response.data,
      success: true,
    };
  },

  getMe: async (): Promise<ApiResponse<AdminUserInfo>> => {
    const response = await apiClient.get(createApiPath.auth.me());

    return {
      data: response.data,
      success: true,
    };
  },

  /** 본인 이름·연락처 수정 — PATCH /admin/auth/me */
  updateMe: async (
    body: AdminMyAccountUpdateRequest,
  ): Promise<ApiResponse<AdminUserInfo>> => {
    const response = await apiClient.patch(createApiPath.auth.me(), body);
    return {
      data: response.data,
      success: true,
    };
  },

  /**
   * 비밀번호 변경 — PUT /admin/auth/password
   * 성공 시 현재 세션용 access/refresh token이 새로 발급된다.
   */
  changePassword: async (
    body: AdminChangePasswordRequest,
  ): Promise<ApiResponse<AuthTokenResponse>> => {
    const response = await apiClient.put(createApiPath.auth.password(), body);
    return {
      data: response.data,
      success: true,
    };
  },

  /**
   * 서버 세션 만료 — POST /admin/auth/logout
   * 현재 access token 세션만 종료한다(동일 계정의 다른 세션은 유지).
   */
  logout: async (): Promise<void> => {
    await apiClient.post(createApiPath.auth.logout());
  },

  /** 클라이언트에 남은 토큰·persist 세션 제거 */
  clearSession: () => {
    localStorage.removeItem('auth-token');
    localStorage.removeItem('auth-storage');
    localStorage.removeItem('refresh-token');
  },

  setTokens: (accessToken: string, refreshToken: string) => {
    localStorage.setItem('auth-token', accessToken);
    localStorage.setItem('refresh-token', refreshToken);
  },

  getTokens: () => {
    return {
      accessToken: localStorage.getItem('auth-token'),
      refreshToken: localStorage.getItem('refresh-token'),
    };
  },

  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('auth-token');
    if (!token) return false;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      return payload.exp > currentTime;
    } catch {
      return false;
    }
  },
};
